import classNames from 'volta/helpers/class-names';
import orElse from 'volta/helpers/or-else';
import BvLabelled from 'volta/components/bv-labelled';
import BvSelectOptions from 'volta/components/bv-select/options';
import BvSelectMultiTrigger from './trigger';
import { concat, get } from '@ember/helper';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import InfinityModel from 'ember-infinity/lib/infinity-model';
import t from 'ember-intl/helpers/t';
import PowerSelectMultiple from 'ember-power-select/components/power-select-multiple';
import and from 'ember-truth-helpers/helpers/and';
import or from 'ember-truth-helpers/helpers/or';
import isEqual from 'lodash/isEqual';

import type { BvBtnArgs } from './../bv-btn';
import type { ComponentLike, ContentValue } from '@glint/template';
import type { Dropdown } from 'ember-basic-dropdown/components/basic-dropdown';
import type { IntlService } from 'ember-intl';
import type { PowerSelectArgs, Select } from 'ember-power-select/components/power-select';
import type { BvLabelledSignature, ILabelAction } from 'volta/components/bv-labelled';
import type { TSize } from 'volta/utils/style-utils';

type ComponentArgs = Pick<
  BvLabelledSignature['Args'],
  | 'error'
  | 'warning'
  | 'helpText'
  | 'isEdited'
  | 'isRequired'
  | 'label'
  | 'labelHidden'
  | 'labelWrapperClass'
> &
  Omit<PowerSelectArgs, 'onChange' | 'onFocus' | 'onBlur' | 'options'>;

export interface BvSelectMultiArgs extends ComponentArgs {
  allowClear?: boolean;
  bufferSize?: number;
  dataName?: string;
  defaultSize?: 'md';
  disabled?: boolean;
  dropdownClass?: string;
  dropdownSize?: TSize;
  extra?: ContentValue;
  fill?: boolean;
  focused?: boolean;
  id?: string;
  infiniteScroll?: boolean;
  isLoading?: boolean;
  labelAction?: ILabelAction;
  labelClass?: string;
  labelInfo?: ContentValue | ComponentLike;
  labelInline?: boolean;
  opened?: boolean;
  options?: any[] | PromiseProxy<any[]> | InfinityModel;
  optionHeight?: number;
  optionKey?: string;
  optionPath?: string;
  resourceNamePlural?: string;
  searchEnabled?: boolean;
  selectedPath?: string;
  size?: BvBtnArgs['size'];
  tabIndex?: number;
  renderAll?: boolean;
  onKeyDown?: (event: KeyboardEvent) => boolean;
  // Callback when selection is changed
  onChange?: (selection: any[], select: Select, event?: Event) => void;
  // Callback when select is focused
  onFocus?: (select: Select, event: FocusEvent) => void;
  // Callback when focus is removed
  onBlur?: (select: Select, event: FocusEvent) => void;
}

interface BvSelectMultiSignature {
  Args: BvSelectMultiArgs;
  Blocks: {
    default: [any, Dropdown];
  };
  Element: HTMLElement;
}

export default class BvSelectMultiComponent extends Component<BvSelectMultiSignature> {
  <template>
    <BvLabelled
      @action={{@labelAction}}
      @dataTestLabelled='bv-select-multi'
      @error={{@error}}
      @fullWidth={{@fill}}
      @helpText={{@helpText}}
      @id={{this.id}}
      @info={{@labelInfo}}
      @inline={{@labelInline}}
      @isEdited={{@isEdited}}
      @isRequired={{@isRequired}}
      @label={{@label}}
      @labelHidden={{@labelHidden}}
      @labelWrapperClass={{@labelWrapperClass}}
      @warning={{@warning}}
      class={{@labelClass}}
      ...attributes
    >
      {{#let
        (component
          BvSelectOptions
          infiniteScroll=@infiniteScroll
          options=@options
          infiniteModel=this.infinityModel
          isLoading=@isLoading
          optionHeight=@optionHeight
          optionKey=(orElse @optionKey @optionPath)
          bufferSize=@bufferSize
          extra=@extra
          renderAll=@renderAll
        )
        (component
          BvSelectMultiTrigger
          size=@size
          fill=@fill
          selectedPath=(or @optionPath @selectedPath)
          isLoading=@isLoading
          isRequired=@isRequired
          onKeydown=@onKeyDown
        )
        as |OptionsComponent TriggerComponent|
      }}
        <PowerSelectMultiple
          @afterOptionsComponent={{@afterOptionsComponent}}
          @allowClear={{@allowClear}}
          @ariaDescribedBy={{@ariaDescribedBy}}
          @ariaInvalid={{@ariaInvalid}}
          @ariaLabel={{@ariaLabel}}
          @ariaLabelledBy={{@ariaLabelledBy}}
          {{! @glint-expect-error: Types are wrong, it's nullable }}
          @beforeOptionsComponent={{null}}
          @destination={{@destination}}
          @closeOnSelect={{@closeOnSelect}}
          @disabled={{@disabled}}
          @dropdownClass={{classNames
            'bv-select__content'
            @dropdownClass
            (if (and @renderInPlace @matchTriggerWidth @fill) 'bv-select__content--fill')
            (if @dropdownSize (concat 'bv-select__content--' @dropdownSize))
          }}
          @eventType={{orElse @eventType 'click'}}
          @extra={{@extra}}
          @loadingMessage={{@loadingMessage}}
          @matcher={{@matcher}}
          @matchTriggerWidth={{if @dropdownSize false @matchTriggerWidth}}
          @noMatchesMessage={{orElse
            @noMatchesMessage
            (t
              'bvList.emptySearchResultTitle'
              resourceNamePlural=(t (or @resourceNamePlural 'items'))
            )
          }}
          @onChange={{this.onChange}}
          @onClose={{@onClose}}
          @onFocus={{this.onFocus}}
          @onBlur={{this.onBlur}}
          @onKeydown={{@onKeydown}}
          @opened={{@opened}}
          @onOpen={{@onOpen}}
          @options={{this.powerSelectOptions}}
          @optionsComponent={{OptionsComponent}}
          @placeholder={{or @placeholder (t 'typeToFilter')}}
          @preventScroll={{orElse @preventScroll true}}
          @renderInPlace={{@renderInPlace}}
          @registerAPI={{@registerAPI}}
          @search={{@search}}
          @searchEnabled={{this.searchEnabled}}
          @searchField={{or @searchField @optionPath}}
          @searchMessage={{@searchMessage}}
          @searchPlaceholder={{@searchPlaceholder}}
          @selected={{@selected}}
          @selectedItemComponent={{@selectedItemComponent}}
          @tabIndex={{or @tabIndex '-1'}}
          @triggerClass={{classNames
            'bv-select-multi'
            @triggerClass
            (if @fill 'bv-select-multi--fill')
            (if this.focused 'bv-select-multi--focused')
            (if @error 'bv-select-multi--error')
          }}
          @triggerComponent={{TriggerComponent}}
          @typeAheadOptionMatcher={{@typeAheadOptionMatcher}}
          @verticalPosition={{@verticalPosition}}
          @onInput={{@onInput}}
          data-test-bv-select-multi-name={{@dataName}}
          as |option term|
        >
          {{#if (has-block)}}
            {{yield option term}}
          {{else}}
            {{if @optionPath (get option @optionPath) option}}
          {{/if}}
        </PowerSelectMultiple>
      {{/let}}
    </BvLabelled>
  </template>
  @service intl!: IntlService;

  id = this.args.id ?? `BvSelectMulti-${guidFor(this)}`;

  get searchEnabled() {
    return this.args.searchEnabled ?? true;
  }

  get infinityModel() {
    return this.args.options instanceof InfinityModel && this.args.infiniteScroll
      ? this.args.options
      : undefined;
  }

  get powerSelectOptions() {
    return this.args.options as any[] | PromiseProxy<any[]> | undefined;
  }

  @tracked focused = Boolean(this.args.focused);

  // Actions
  // ~~~~~

  @action
  onChange(selection: any[], select: Select, event?: Event) {
    const { selected = [], highlighted = [] } = select;

    this.args.onChange?.(
      // Because we force the inclusion if selectedIds sometimes, we need to filter out the highlighted
      isEqual(selected, highlighted)
        ? selection.filter((o) => !isEqual(highlighted, o))
        : selection,
      select,
      event
    );
  }

  @action
  onFocus(select: Select, event: FocusEvent) {
    this.focused = true;
    this.args.onFocus?.(select, event);
  }

  @action
  onBlur(select: Select, event: FocusEvent) {
    this.focused = false;
    this.args.onBlur?.(select, event);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvSelectMulti: typeof BvSelectMultiComponent;
    'bv-select-multi': typeof BvSelectMultiComponent;
  }
}
